'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { COOKIE_CONSENT_EVENT, isAnalyticsConsent, readCookieConsent } from '@/lib/cookie-consent'
import {
  parseGa4MeasurementId,
  shouldTrackPublicPageView,
} from '@/lib/ga4'
import {
  disableGa4AndClearCookies,
  ensureGtagLoaded,
  sendPublicPageView,
  setActiveGa4MeasurementId,
  setGa4Disabled,
} from '@/lib/ga4-runtime'

let lastTrackedPath: string | null = null

function Ga4AnalyticsActive({ measurementId }: { measurementId: string }) {
  const pathname = usePathname() || '/'

  useEffect(() => {
    setActiveGa4MeasurementId(measurementId)

    function sync(consent: unknown) {
      const track = shouldTrackPublicPageView({ measurementId, consent, pathname })
      if (!track) {
        lastTrackedPath = null
        if (isAnalyticsConsent(consent)) {
          setGa4Disabled(measurementId, true)
        } else {
          disableGa4AndClearCookies()
        }
        return
      }
      ensureGtagLoaded(measurementId, window.location.origin, pathname, document.referrer)
      if (lastTrackedPath === pathname) return
      lastTrackedPath = pathname
      sendPublicPageView(measurementId, window.location.origin, pathname, document.referrer)
    }

    sync(readCookieConsent(window.localStorage))

    function onConsent(event: Event) {
      const detail = (event as CustomEvent<{ consent?: unknown }>).detail
      sync(detail?.consent ?? readCookieConsent(window.localStorage))
    }

    window.addEventListener(COOKIE_CONSENT_EVENT, onConsent)
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onConsent)
  }, [measurementId, pathname])

  return null
}

export function Ga4Analytics({ measurementId }: { measurementId: string | null }) {
  const id = parseGa4MeasurementId(measurementId)
  if (!id) return null
  return <Ga4AnalyticsActive measurementId={id} />
}
